import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { Interpolate, translate } from 'react-i18next';
import { inject, observer } from 'mobx-react';
import b from 'b_';
import BrowserStore from '../../browser/stores/BrowserStore';
import makeElementId from '../../makeElementId';
import FuseWysiwygReadOnly from '../Fuse/FuseWysiwyg/FuseWysiwygReadOnly';
import FuseAlert from '../FuseControls/FuseAlert';
import FuseButton from '../FuseControls/FuseButton';
import TimerSpinner from '../TimerSpinner/TimerSpinner';
import Icon from '../Icon/Icon';
import { ObservableTextfield } from '../Textfield';
import PopUpDetectedController from '../../browser/PopUpDetectedController';
import BrowserPopUp from '../BrowserPopUp/BrowserPopUp';
import { FTBody, FTTitle } from '../Fuse';
import SnackStore from '../../stores/SnackStore';
import { isBrowserFuse5Enabled } from '../../fuseFeatureFlag';
import OldRightNotification from './OldRightNotification';

import './RightNotification.css';

const block = b.lock('RightNotification');

const BrowsingRestriction = ({ title, subTitle, children }) => (
    <Fragment>
        <FTTitle>{title}</FTTitle>
        <div className={block('BRSubTitle')}>{subTitle}</div>
        <FTBody>{children}</FTBody>
    </Fragment>
);
BrowsingRestriction.propTypes = {
    title: PropTypes.string.isRequired,
    subTitle: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
};

class NewRightNotification extends Component {
    static propTypes = {
        t: PropTypes.func.isRequired,
        browser: BrowserStore.PropType.isRequired,
        snack: SnackStore.PropType.isRequired,
    };

    popoverId = makeElementId('rightNotificationCornerPopover');
    titleId = makeElementId('rightNotificationTitle');
    textId = makeElementId('rightNotificationText');
    popUpBodyRef = React.createRef();

    copyToClipboard = async (urlToExit) => {
        const { snack, t } = this.props;
        try {
            await navigator.clipboard.writeText(urlToExit);
            snack.showSuccess(t('Notification_Snack.Copy_URL_Success'));
        } catch (err) {
            snack.showError(<Interpolate
                i18nKey="Notification_Snack.Copy_URL_Failed"
                error={err?.message ?? ''}
            />);
        }
    }

    getPopUpTitle = (notification) => {
        const { browser, t } = this.props;
        if (notification && this[notification] && notification !== BrowserStore.NOTIFICATION.FILE_PROCESSING) {
            if (notification === BrowserStore.NOTIFICATION.INPUT_DISABLED) {
                return t(browser.isHarScanInputRestrictions()
                    ? 'Notification.INPUT_DISABLED.altTitle'
                    : 'Notification.INPUT_DISABLED.title'
                );
            }

            if (notification === BrowserStore.NOTIFICATION.NEW_CONFIRM_EXIT_ISOLATION) {
                return t('UDTopBar.newExitWarning');
            }

            if (notification === BrowserStore.NOTIFICATION.REQUEST_BASIC_AUTHORIZATION) {
                return `${browser.basicAuth.challengerUrl} ${t('Notification.BASIC_AUTHORIZATION.title')}`;
            }

            if (notification === BrowserStore.NOTIFICATION.CUSTOMER_MESSAGE) {
                const chunks = (browser.originalQuery.notification ?? '').split(/[{}]/g);
                if (chunks && chunks[0]) {
                    return chunks[0];
                }
            }

            return t(`Notification.${notification}.title`);
        }

        return t('Notification.DEFAULT_RIGHT.title');
    };

    normalizeNotificationResult = (notification) => {
        const generator = this[notification];
        if (!generator) return null;
        const result = generator();
        if (result && typeof result === 'object' && !Array.isArray(result)
            && Object.prototype.hasOwnProperty.call(result, 'title')
            && Object.prototype.hasOwnProperty.call(result, 'body')) {
            return result;
        }
        return {
            title: this.getPopUpTitle(notification),
            body: result,
            actions: null,
        };
    };

    [BrowserStore.NOTIFICATION.INPUT_DISABLED] = () => {
        const { t, browser } = this.props;
        return {
            title: null,
            body: <BrowsingRestriction
                title={t(browser.isHarScanInputRestrictions()
                    ? 'NotificationV2.INPUT_DISABLED.altTitle'
                    : 'NotificationV2.INPUT_DISABLED.title'
                )}
                subTitle={t('NotificationV2.INPUT_DISABLED.subTitle')}
            >
                <Fragment>
                    <div>
                        {t(browser.isHarScanInputRestrictions()
                            ? 'NotificationV2.INPUT_DISABLED.altText'
                            : 'NotificationV2.INPUT_DISABLED.text'
                        )}
                    </div>
                    <br /><div>{t('NotificationV2.INPUT_DISABLED.text2')}</div>
                </Fragment>
            </BrowsingRestriction>,
            actions: <FuseButton
                role="button"
                type="primary"
                color="blue"
                onClick={() => this.props.browser.closeNotification()}
            >
                {t('NotificationV2.BrowsingRestrictions.action')}
            </FuseButton>
        };
    };

    [BrowserStore.NOTIFICATION.PASTE_DISABLED] = () => {
        return {
            title: null,
            body: <BrowsingRestriction
                title={this.props.t('NotificationV2.BrowsingRestrictions.title')}
                subTitle={this.props.t('NotificationV2.PASTE_DISABLED.subTitle')}
            >
                <Fragment>
                    <div>{this.props.t('NotificationV2.BrowsingRestrictions.text')}</div>
                    <br /><div>{this.props.t('NotificationV2.PASTE_DISABLED.text')}</div>
                </Fragment>
            </BrowsingRestriction>,
            actions: <FuseButton
                role="button"
                type="primary"
                color="blue"
                onClick={() => this.props.browser.closeNotification()}
            >
                {this.props.t('NotificationV2.BrowsingRestrictions.action')}
            </FuseButton>,
        };
    };

    [BrowserStore.NOTIFICATION.PASTE_TRUNCATED] = () => {
        return {
            title: null,
            body: <BrowsingRestriction
                title={this.props.t('NotificationV2.BrowsingRestrictions.title')}
                subTitle={this.props.t('NotificationV2.PASTE_TRUNCATED.subTitle')}
            >
                <Fragment>
                    <div>{this.props.t('NotificationV2.BrowsingRestrictions.text')}</div>
                    <br /><div>{this.props.t('NotificationV2.PASTE_TRUNCATED.text')}</div>
                </Fragment>
            </BrowsingRestriction>,
            actions: <FuseButton
                role="button"
                type="primary"
                color="blue"
                onClick={() => this.props.browser.closeNotification()}
            >
                {this.props.t('NotificationV2.BrowsingRestrictions.action')}
            </FuseButton>,
        };
    };

    [BrowserStore.NOTIFICATION.CONFIRM_EXIT_ISOLATION] = () => {
        const { t, browser } = this.props;
        return {
            title: t('NotificationV2.CONFIRM_EXIT_ISOLATION.title'),
            body: t('NotificationV2.CONFIRM_EXIT_ISOLATION.text'),
            actions: <div className={block('InfoActions')}>
                <FuseButton
                    role="button"
                    type={'secondary'}
                    color={'blue'}
                    onClick={() => browser.navigateToOriginalUrl()}
                >{t('NotificationV2.CONFIRM_EXIT_ISOLATION.action1')}</FuseButton>
                <FuseButton
                    role="button"
                    type={'primary'}
                    color={'blue'}
                    onClick={() => browser.closeNotification()}
                >{t('NotificationV2.CONFIRM_EXIT_ISOLATION.action2')}</FuseButton>
            </div>,
        };
    }

    [BrowserStore.NOTIFICATION.COPY_URL_TO_EXIT] = () => {
        const { t, browser } = this.props;
        const urlToExit = browser.urlInputValue.trim().length === 0 ?
            browser.getOriginalUrlByClickId(browser.sandboxClickId) : browser.urlInputValue;
        return {
            title: t('NotificationV2.COPY_URL_TO_EXIT.title'),
            body: t('NotificationV2.COPY_URL_TO_EXIT.text'),
            actions: <FuseButton
                role="button"
                type={FuseButton.type.PRIMARY}
                color={FuseButton.color.BLUE}
                onClick={() => {
                    browser.closeNotification();
                    this.copyToClipboard(urlToExit);
                }}
            >{t('NotificationV2.COPY_URL_TO_EXIT.action')}</FuseButton>,
        };
    };

    [BrowserStore.NOTIFICATION.NEW_CONFIRM_EXIT_ISOLATION] = () => {
        const { t, browser } = this.props;
        let exitMsg = t('NotificationV2.NEW_CONFIRM_EXIT_ISOLATION.text');
        if (browser.exitWarningText) {
            if (typeof browser.exitWarningText !== 'string') {
                exitMsg = <FuseWysiwygReadOnly content={browser.exitWarningText}/>;
            } else {
                exitMsg = browser.exitWarningText;
            }
        }
        return {
            title: null,
            body: <FuseAlert
                id={this.titleId}
                role="alert"
                type="warning"
                icon="warning"
                tabIndex="0"
                style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                }}
            >{exitMsg}</FuseAlert>,
            actions: <div className={block('InfoActions')}>
                <FuseButton
                    role="button"
                    type={'secondary'}
                    color={'blue'}
                    onClick={() => {
                        this.props.browser.closeNotification();
                        this.props.browser.navigateToOriginalUrl();
                    }}
                    autoFocus={!this.props.browser.isConsolePreview}
                >{t('NotificationV2.NEW_CONFIRM_EXIT_ISOLATION.action1')}</FuseButton>
                <FuseButton
                    role="button"
                    type={'primary'}
                    color={'blue'}
                    onClick={() => this.props.browser.closeNotification()}
                >{t('NotificationV2.NEW_CONFIRM_EXIT_ISOLATION.action2')}</FuseButton>
            </div>,
        };
    };

    [BrowserStore.NOTIFICATION.CLIPBOARD_PERMISSION] = () => {
        const { t, browser } = this.props;
        return {
            title: t('NotificationV2.CLIPBOARD_PERMISSION.title'),
            body: t('NotificationV2.CLIPBOARD_PERMISSION.text'),
            actions: <div className={block('InfoActions')}>
                <FuseButton
                    role="button"
                    type={'secondary'}
                    color={'blue'}
                    onClick={() => browser.closeNotification()}
                >{t('NotificationV2.CLIPBOARD_PERMISSION.action1')}</FuseButton>
                <FuseButton
                    role="button"
                    type={'primary'}
                    color={'blue'}
                    onClick={() => browser.allowSetClipboard()}
                >{t('NotificationV2.CLIPBOARD_PERMISSION.action2')}</FuseButton>
            </div>,
        };
    }

    [BrowserStore.NOTIFICATION.FULL_SCREEN_PERMISSION] = () => {
        const { t, browser } = this.props;
        return {
            title: t('NotificationV2.FULL_SCREEN_PERMISSION.title'),
            body: t('NotificationV2.FULL_SCREEN_PERMISSION.text'),
            actions: <div className={block('InfoActions')}>
                <FuseButton
                    role="button"
                    type={'secondary'}
                    color={'blue'}
                    onClick={() => browser.exitFullscreen()}
                >{t('NotificationV2.FULL_SCREEN_PERMISSION.action1')}</FuseButton>
                <FuseButton
                    role="button"
                    type={'primary'}
                    color={'blue'}
                    onClick={() => browser.openFullscreen()}
                >{t('NotificationV2.FULL_SCREEN_PERMISSION.action2')}</FuseButton>
            </div>,
        };
    }

    [BrowserStore.NOTIFICATION.EMAIL_PERMISSION] = () => {
        const { t, browser } = this.props;
        return {
            title: t('NotificationV2.EMAIL_PERMISSION.title'),
            body: t('NotificationV2.EMAIL_PERMISSION.text'),
            actions: <div className={block('InfoActions')}>
                <FuseButton
                    role="button"
                    type={'secondary'}
                    color={'blue'}
                    onClick={() => browser.closeNotification()}
                >{t('NotificationV2.EMAIL_PERMISSION.action1')}</FuseButton>
                <FuseButton
                    role="button"
                    type={'primary'}
                    color={'blue'}
                    onClick={() => browser.openMailClient()}
                >{t('NotificationV2.EMAIL_PERMISSION.action2')}</FuseButton>
            </div>,
        };
    }

    [BrowserStore.NOTIFICATION.REQUEST_BASIC_AUTHORIZATION] = () => {
        const { t, browser } = this.props;
        const onSubmit = (event) => {
            event.preventDefault();
            browser.sendBasicAuth();
        };
        return {
            title: null,
            body: <form onSubmit={onSubmit}>
                <div >
                    <div className={block('InfoTitle')} id={this.titleId}>
                        {browser.basicAuth.challengerUrl} {t('NotificationV2.BASIC_AUTHORIZATION.title')}
                    </div>
                    <div className={block('InfoText')}>
                        <ObservableTextfield
                            className={block('Field')}
                            label={t('NotificationV2.BASIC_AUTHORIZATION.username')}
                            fullWidth
                            store={browser.basicAuth.username}
                        />
                        <ObservableTextfield
                            className={block('Field')}
                            label={t('NotificationV2.BASIC_AUTHORIZATION.password')}
                            type="password"
                            fullWidth
                            store={browser.basicAuth.password}
                        />
                    </div>
                </div>
            </form>,
            actions: <div className={block('InfoActions')}>
                <FuseButton
                    role="button"
                    type={FuseButton.type.SECONDARY}
                    color={FuseButton.color.BLUE}
                    onClick={() => browser.cancelBasicAuth()}
                >{t('NotificationV2.BASIC_AUTHORIZATION.cancel')}</FuseButton>
                <FuseButton
                    role="button"
                    type={FuseButton.type.PRIMARY}
                    color={FuseButton.color.BLUE}
                    onClick={() => browser.sendBasicAuth()}
                >{t('NotificationV2.BASIC_AUTHORIZATION.submit')}</FuseButton>
            </div>,
        };
    }

    [BrowserStore.NOTIFICATION.PAUSING_SESSION] = () => {
        const { t, browser } = this.props;
        return {
            title: t('NotificationV2.PAUSING_SESSION.title'),
            body: t('NotificationV2.PAUSING_SESSION.text'),
            actions: <div className={block('InfoBottomSection')}>
                <div className={block('InfoBottomTextContainer')}>
                    <TimerSpinner
                        totalTime={browser.inactivity.timeTotal}
                        currentTime={browser.inactivity.timeLeft}
                        className={block('ActionSectionIcon')}
                        counterClockwise
                    />
                    <span className={block('InfoBottomText')}>
                        {browser.inactivity.timeLeft + ' ' + t('Notification.PAUSING_SESSION.seconds')}
                    </span>
                </div>
                <FuseButton
                    role="button"
                    type={FuseButton.type.PRIMARY}
                    color={FuseButton.color.BLUE}
                    onClick={() => { /* InactivityStore has listener on body */
                    }}
                >{t('Notification.PAUSING_SESSION.action')}</FuseButton>
            </div>,
        };
    };

    [BrowserStore.NOTIFICATION.PAUSING_SESSION_CANCELLED] = () => {
        const { t } = this.props;
        return {
            title: t('NotificationV2.PAUSING_SESSION_CANCELLED.title'),
            body: t('NotificationV2.PAUSING_SESSION_CANCELLED.text'),
            actions: <div className={block('InfoBottomSection')}>
                <div className={block('InfoBottomText')}>
                    {t('Notification.PAUSING_SESSION_CANCELLED.message')}
                </div>
            </div>,
        };
    };

    [BrowserStore.NOTIFICATION.SESSION_PAUSED] = () => {
        const { t } = this.props;
        return {
            title: t('NotificationV2.SESSION_PAUSED.title'),
            body: <div id={this.textId}>
                <Interpolate
                    i18nKey="NotificationV2.SESSION_PAUSED.text.template"
                    linebreak={<Fragment><br/><br/></Fragment>}
                    highlight={<strong>{t('NotificationV2.SESSION_PAUSED.text.highlight')}</strong>}
                />
            </div>,
            actions: <div className={block('InfoBottomSection')}>
                <div className={block('InfoBottomTextContainer')}>
                    <Icon
                        className={block('ActionSectionIcon', { red: true })}
                        type="pause_circle_outline"
                    />
                    <span className={block('InfoBottomText')}>{t('NotificationV2.SESSION_PAUSED.message')}</span>
                </div>
                <FuseButton
                    role="button"
                    type={FuseButton.type.PRIMARY}
                    color={FuseButton.color.BLUE}
                    onClick={() => window.top.location.reload()}
                >
                    {t('NotificationV2.SESSION_PAUSED.action')}
                </FuseButton>
            </div>
        };
    };

    [BrowserStore.NOTIFICATION.ANOTHER_TAB_THREAT_DETECTED] = () => {
        const { t } = this.props;
        return {
            title: t('NotificationV2.ANOTHER_TAB_THREAT_DETECTED.title'),
            body: t('NotificationV2.ANOTHER_TAB_THREAT_DETECTED.text'),
            actions: <FuseButton
                role="button"
                type={FuseButton.type.PRIMARY}
                color={FuseButton.color.BLUE}
                onClick={() => window.top.location.reload()}
            >{t('NotificationV2.ANOTHER_TAB_THREAT_DETECTED.action')}</FuseButton>,
        };
    };

    [BrowserStore.NOTIFICATION.POPUP_DETECTED] = () => {
        const { t, browser } = this.props;
        return {
            title: <div id={this.titleId}>
                {t('NotificationV2.POPUP_DETECTED.title')}
            </div>,
            body: <div id={this.textId}>
                <Interpolate
                    i18nKey="NotificationV2.POPUP_DETECTED.text"
                    linebreak={<Fragment><br/><br/></Fragment>}
                    domain={<strong><br/>{window.location.hostname}</strong>}
                />
            </div>,
            actions: <div className={block('InfoActions')}>
                <FuseButton
                    role="button"
                    type={'secondary'}
                    color={'blue'}
                    onClick={() => browser.closeNotification()}
                >{t('NotificationV2.POPUP_DETECTED.action1')}</FuseButton>
                <PopUpDetectedController
                    onWindowDeny={() => this.props.browser.closeNotification()}
                    url={browser.popupData.browserUrl}
                    render={(hrefToNewWindow) => (
                        <FuseButton
                            href={hrefToNewWindow}
                            target="_blank"
                            type={'primary'}
                            color={'blue'}
                            onClick={() => this.props.browser.closeNotification()}
                        >{t('NotificationV2.POPUP_DETECTED.action2')}</FuseButton>
                    )}
                />
            </div>,
        };
    };

    [BrowserStore.NOTIFICATION.CONNECTION_ESTABLISHED] = () => {
        const { t } = this.props;
        return {
            title: t('NotificationV2.CONNECTION_ESTABLISHED.title'),
            body: t('NotificationV2.CONNECTION_ESTABLISHED.text'),
            actions: <FuseButton
                role="button"
                type={FuseButton.type.PRIMARY}
                color={FuseButton.color.BLUE}
                onClick={() => this.props.browser.closeNotification()}
            >{t('NotificationV2.CONNECTION_ESTABLISHED.action')}</FuseButton>,
        };
    };

    [BrowserStore.NOTIFICATION.TRYING_TO_CONNECT] = () => {
        const { t } = this.props;
        return {
            title: t('NotificationV2.TRYING_TO_CONNECT.title'),
            body: <div id={this.titleId}>
                <Interpolate
                    i18nKey={'Notification.TRYING_TO_CONNECT.text.template'}
                    linebreak={<Fragment><br/><br/></Fragment>}
                    highlight={<strong>{t('NotificationV2.TRYING_TO_CONNECT.text.highlight')}</strong>}
                    list={<ul className={block('InfoBlockList')}>
                        <li>{t('NotificationV2.TRYING_TO_CONNECT.text.listItem1')}</li>
                        <li>{t('NotificationV2.TRYING_TO_CONNECT.text.listItem2')}</li>
                    </ul>}
                />
            </div>,
            actions: <div className={block('InfoBottomSection')}>
                <div className={block('InfoBottomTextContainer')}>
                    <Icon
                        className={block('ActionSectionIcon', { sync: true })}
                        type={'sync'}
                    />
                    <span className={block('InfoBottomText')}>{t('Notification.TRYING_TO_CONNECT.message')}</span>
                </div>
            </div>,
        };
    };

    [BrowserStore.NOTIFICATION.DISCONNECTED] = () => {
        const { t } = this.props;
        return {
            title: t('NotificationV2.DISCONNECTED.title'),
            body: t('NotificationV2.DISCONNECTED.text1'),
            actions: <div className={block('DisconectedActionsWrapper')}>
                <div className={block('DisconectedActionText')}>
                    <Icon
                        type="no_wifi"
                        className={block('DisconectedIcon')}
                    />
                    <FTBody className={block('DisconectedIconText')}>{t('NotificationV2.DISCONNECTED.text2')}</FTBody>
                </div>
                <FuseButton
                    role="button"
                    type={'primary'}
                    color={'blue'}
                    onClick={() => window.top.location.reload()}
                >{t('NotificationV2.DISCONNECTED.action')}</FuseButton>
            </div>,
        };
    };

    [BrowserStore.NOTIFICATION.CUSTOMER_MESSAGE] = () => {
        const { browser, t } = this.props;
        const { notification = '', notificationLink } = browser.originalQuery;
        const chunks = notification.split(/[{}]/g);
        return {
            title: null,
            body: <div>
                <div style={{ marginBottom: 0 }}>
                    <span id={this.titleId}>{chunks[0]}</span>
                    <span>{notificationLink && chunks[1] ? <a
                        href={notificationLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#0277BA', textDecoration: 'underline' }}
                    >{chunks[1]}</a> : chunks[1]}</span>
                    <span id={this.textId}>{chunks[2]}</span>
                </div>
            </div>,
            actions: <FuseButton
                role="button"
                type={FuseButton.type.PRIMARY}
                color={FuseButton.color.BLUE}
                onClick={() => this.props.browser.closeNotification()}
            >{t('NotificationV2.CUSTOMER_MESSAGE.action')}</FuseButton>,
        };
    };

    [BrowserStore.NOTIFICATION.LOCAL_APPLICATION_BLOCKED] = () => {
        const { t } = this.props;
        return {
            title: t('NotificationV2.LOCAL_APPLICATION_BLOCKED.title'),
            body: t('NotificationV2.LOCAL_APPLICATION_BLOCKED.text'),
            actions: <FuseButton
                role="button"
                type={FuseButton.type.PRIMARY}
                color={FuseButton.color.BLUE}
                onClick={() => this.props.browser.closeNotification()}
            >{t('NotificationV2.BrowsingRestrictions.action')}</FuseButton>,
        };
    };

    [BrowserStore.NOTIFICATION.DOWNLOADS_DISABLED] = () => {
        const { t } = this.props;
        return {
            title: t('NotificationV2.DOWNLOADS_DISABLED.title'),
            body: t('NotificationV2.DOWNLOADS_DISABLED.text'),
            actions: <FuseButton
                role="button"
                type={FuseButton.type.PRIMARY}
                color={FuseButton.color.BLUE}
                onClick={() => this.props.browser.closeNotification()}
            >{t('NotificationV2.DOWNLOADS_DISABLED.action')}</FuseButton>,
        };
    };

    [BrowserStore.NOTIFICATION.UPLOADS_DISABLED] = () => {
        const { t } = this.props;
        return {
            title: t('NotificationV2.UPLOADS_DISABLED.title'),
            body: t('NotificationV2.UPLOADS_DISABLED.text'),
            actions: <FuseButton
                role="button"
                type={FuseButton.type.PRIMARY}
                color={FuseButton.color.BLUE}
                onClick={() => this.props.browser.closeNotification()}
            >{t('NotificationV2.UPLOADS_DISABLED.action')}</FuseButton>,
        };
    };

    [BrowserStore.NOTIFICATION.ALL_COPY_RESTRICTIONS] = () => {
        return {
            title: null,
            body: <BrowsingRestriction
                title={this.props.t('NotificationV2.BrowsingRestrictions.title')}
                subTitle={this.props.t('NotificationV2.ALL_COPY_RESTRICTIONS.subTitle')}
            >
                <Fragment>
                    <div>{this.props.t('NotificationV2.BrowsingRestrictions.text')}</div>
                    <br /><div>{this.props.t('NotificationV2.ALL_COPY_RESTRICTIONS.text')}</div>
                </Fragment>
            </BrowsingRestriction>,
            actions: <FuseButton
                role="button"
                type="primary"
                color="blue"
                onClick={() => this.props.browser.closeNotification()}
            >
                {this.props.t('NotificationV2.BrowsingRestrictions.action')}
            </FuseButton>,
        };
    };

    [BrowserStore.NOTIFICATION.COPY_ON_CLICK_RESTRICTIONS] = () => {
        return {
            title: null,
            body: <BrowsingRestriction
                title={this.props.t('NotificationV2.BrowsingRestrictions.title')}
                subTitle={this.props.t('NotificationV2.COPY_ON_CLICK_RESTRICTIONS.subTitle')}
            >
                <Fragment>
                    <div>{this.props.t('NotificationV2.BrowsingRestrictions.text')}</div>
                    <br /><div>{this.props.t('NotificationV2.COPY_ON_CLICK_RESTRICTIONS.text')}</div>
                </Fragment>
            </BrowsingRestriction>,
            actions: <FuseButton
                role="button"
                type="primary"
                color="blue"
                onClick={() => this.props.browser.closeNotification()}
            >
                {this.props.t('NotificationV2.BrowsingRestrictions.action')}
            </FuseButton>,
        };
    };

    onClickOutside = () => {
        const { browser } = this.props;

        if ([
            BrowserStore.NOTIFICATION.DEFAULT_RIGHT,
            BrowserStore.NOTIFICATION.NEW_CONFIRM_EXIT_ISOLATION,
        ].includes(browser.notification)) {
            browser.closeNotification();
        }
    };

    render() {
        const { browser } = this.props;
        const notification = browser.notification;
        const isOpen = !!(notification && this[notification]);
        if (!isOpen) return null;
        const normalized = this.normalizeNotificationResult(notification);
        return <BrowserPopUp
            isOpen={isOpen}
            onRequestClose={this.onClickOutside}
            onClose={this.onClickOutside}
            title={normalized?.title ?? ''}
            contentLabel={normalized?.title ?? ''}
            shouldCloseOnOverlayClick
            shouldCloseOnEsc
            footerActions={normalized?.actions}
        >
            <FTBody ref={this.popUpBodyRef}>
                {normalized?.body}
            </FTBody>
        </BrowserPopUp>;
    }
}

const WrappedRightNotification = translate()(inject('browser', 'snack')(observer(NewRightNotification)));

const RightNotification = (props) => {
    return isBrowserFuse5Enabled() ? <WrappedRightNotification {...props}/> : <OldRightNotification {...props}/>;
};

export default RightNotification;
